import { Button, RiCompass3Line, Stack } from '@landler/component-library';
import bbox from '@turf/bbox';
import { BBox2d } from '@turf/helpers/dist/js/lib/geojson';
import { bboxPolygon, circle, difference, featureCollection } from '@turf/turf';
import { BBox, Feature, FeatureCollection, Point, Polygon } from 'geojson';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layer, MapRef, Source } from 'react-map-gl';

import { FormRadioGroup, FormRadioItem } from '@/components/widgets/o/Form/FormRadio';
import { useControlsContext } from '@/components/widgets/o/Map/hooks/useFormControls';
import { SectionTitle, SectionWrapper } from '@/components/widgets/o/Map/LayerEditMenu/Sections';
import { useIsPlotPage } from '@/hooks/useIsPlotPage';
import { useLandscapeView } from '@/hooks/useLandscapeView';
import { isNullish } from '@/utils/booleans';

import { getRadiusCircleMask } from '../hooks/useBoundingBox';
import { NO_RADIUS, RadiusType, useMapRadiusContext } from './MapRadiusContextProvider';

const SHOW_RADIUS_LABEL = 'shared.projects.map.labels.showRadius';
export const RADIUS_OPTIONS: RadiusType[] = ['5', '10', '15', '20', '25', '30'];

export type ReportingBoundsLayerProps = {
  bounds: BBox2d;
  mapRef: MapRef | null;
  enableZoom?: boolean;
};

export const ReportingBoundsPicker = ({ bounds, mapRef, enableZoom = true }: ReportingBoundsLayerProps) => {
  const controlsForm = useControlsContext();
  const [radius] = controlsForm.watch(['radius']);

  const hasRadius = radius !== NO_RADIUS && !isNullish(radius);

  const [showRadius, setShowRadius] = useState(hasRadius);

  useEffect(() => {
    if (hasRadius && !showRadius) {
      setShowRadius(true);

      const radiusKm = Number(radius);
      const { circleGeoJSON } = getRadiusCircleMask({ bounds, radius: radiusKm });
      const circleBbox = bbox(circleGeoJSON);
      if (enableZoom) updateMapBoundaries({ mapRef, bounds: circleBbox });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRadius]);

  return (
    <Stack className='flex flex-row gap-2'>
      <RadiusComponent
        showRadius={showRadius}
        setShowRadius={setShowRadius}
        bounds={bounds}
        mapRef={mapRef}
        enableZoom={enableZoom}
      />
    </Stack>
  );
};

const RadiusComponent = ({
  showRadius,
  setShowRadius,
  bounds,
  mapRef,
  enableZoom,
}: {
  showRadius: boolean;
  setShowRadius: (show: boolean) => void;
} & ReportingBoundsLayerProps) => {
  const controlsForm = useControlsContext();
  const [radius] = controlsForm.watch(['radius']);
  const radiusKm = isNullish(radius) || radius === NO_RADIUS ? null : Number(radius);

  const { circleGeoJSON } = getRadiusCircleMask({ bounds, radius: radiusKm });

  const hasPageLandscapeView = useIsPlotPage(); // Only plot pages have the landscape view

  const handleRadiusButtonClick = () => {
    const newShowRadius = !showRadius;
    setShowRadius(newShowRadius);

    if (newShowRadius) {
      if (!circleGeoJSON) return;

      const circleBbox = bbox(circleGeoJSON);
      if (enableZoom) updateMapBoundaries({ mapRef, bounds: circleBbox });
    } else if (enableZoom) updateMapBoundaries({ mapRef, bounds });
  };

  const handleRadiusRadioButtonChange = (newRadius: RadiusType) => {
    const { circleGeoJSON: newCircleGeoJSON } = getRadiusCircleMask({
      bounds,
      radius: newRadius === NO_RADIUS ? null : Number(newRadius),
    });

    setShowRadius(true);

    const circleBbox = bbox(newCircleGeoJSON);
    if (enableZoom) updateMapBoundaries({ mapRef, bounds: circleBbox });
  };

  const handleRadiusRadioButtonReset = () => {
    setShowRadius(false);
    if (enableZoom) updateMapBoundaries({ mapRef, bounds });

    if (radius !== NO_RADIUS) {
      controlsForm.setValue('radius', NO_RADIUS);
    }
  };

  if (!hasPageLandscapeView) {
    return <ShowRadiusButton onClick={handleRadiusButtonClick} />;
  }

  return (
    <ShowRadiusComponent
      onRadiusRadioButtonChange={handleRadiusRadioButtonChange}
      onRadiusRadioButtonReset={handleRadiusRadioButtonReset}
    />
  );
};

export const ReportingBoundsLayer = ({ plotMarkers }: { plotMarkers?: FeatureCollection<Point> }) => {
  const controlsForm = useControlsContext();
  const [radius] = controlsForm.watch(['radius']);

  if (isNullish(radius) || radius === NO_RADIUS) {
    return null;
  }

  const radiusKm = Number(radius);

  const circleFeatures =
    plotMarkers?.features.map((feature) => {
      const coords = feature.geometry.coordinates;
      return circle(coords, radiusKm, { steps: 64, units: 'kilometers' });
    }) ?? [];

  if (circleFeatures.length <= 0) return null;

  const world = bboxPolygon([-180, -90, 180, 90]);

  let allCirclesDifference = world;
  allCirclesDifference = circleFeatures.reduce((acc, curr) => {
    const diff = difference(acc, curr) as Feature<Polygon>;
    if (diff) return diff;
    return acc;
  }, allCirclesDifference);

  if (isNullish(allCirclesDifference)) return null;

  const mask = featureCollection([allCirclesDifference as Feature<Polygon>]);

  return (
    <Source id='mask-source' type='geojson' data={mask}>
      <Layer
        id='mask-layer'
        type='fill'
        paint={{
          'fill-color': '#000000',
          'fill-opacity': 0.7,
        }}
      />
    </Source>
  );
};

export const initializeMapZoom = ({
  bounds,
  mapRef,
  radius,
}: {
  radius: string;
} & ReportingBoundsLayerProps) => {
  const radiusKm = isNullish(radius) || radius === NO_RADIUS ? null : Number(radius);

  if (!radiusKm) return;

  const { circleGeoJSON } = getRadiusCircleMask({ bounds, radius: radiusKm });
  const circleBbox = bbox(circleGeoJSON);

  if (!circleBbox) return;

  updateMapBoundaries({ mapRef, bounds: circleBbox });
};

const updateMapBoundaries = ({ mapRef, bounds }: { bounds: BBox | BBox2d; mapRef: MapRef | null }) => {
  if (!mapRef) return;

  mapRef.fitBounds(
    [
      [bounds[0], bounds[1]],
      [bounds[2], bounds[3]],
    ],
    {
      padding: 50,
      duration: 1000,
    },
  );
};

const ShowRadiusComponent = ({
  onRadiusRadioButtonChange,
  onRadiusRadioButtonReset,
}: {
  onRadiusRadioButtonChange: (newRadius: RadiusType) => void;
  onRadiusRadioButtonReset: () => void;
}) => {
  const isLandsCapeViewActive = useLandscapeView().isActive;

  const onResetRef = useRef(onRadiusRadioButtonReset); // Prevent zoom reset on every component re-render
  const wasActiveRef = useRef(false);

  useEffect(() => {
    if (isLandsCapeViewActive) {
      wasActiveRef.current = true;
    } else if (wasActiveRef.current) {
      onResetRef.current();
    }
  }, [isLandsCapeViewActive]);

  if (!isLandsCapeViewActive) {
    return null;
  }

  return <ShowRadiusDropdown onRadioButtonClick={onRadiusRadioButtonChange} onReset={onRadiusRadioButtonReset} />;
};

const ShowRadiusButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();

  const RADIUS = 30 as const;

  return (
    <Button
      onClick={onClick}
      variant='contained'
      color='white'
      size='small'
      className='text-text-primary'
      leftAdornment={<RiCompass3Line />}
      data-testid='show-radius-button'
    >
      {t(SHOW_RADIUS_LABEL, { radius: RADIUS })}
    </Button>
  );
};

const ShowRadiusDropdown = ({
  onRadioButtonClick,
  onReset,
}: {
  onRadioButtonClick: (newRadius: RadiusType) => void;
  onReset: () => void;
}) => {
  const { setRadius } = useMapRadiusContext();

  const { t } = useTranslation();

  const controlsForm = useControlsContext();

  const handleRadiusRadioButtonClick = (value: string | boolean | null | undefined) => {
    if (typeof value !== 'string') return;

    setRadius(value as RadiusType);

    if (value === NO_RADIUS) {
      onReset();
    } else {
      onRadioButtonClick(value as RadiusType);
    }
  };

  return (
    <SectionWrapper>
      <SectionTitle text={t('shared.projects.map.labels.radius')} />
      <FormRadioGroup name='radius' onValueChange={handleRadiusRadioButtonClick} controlsform={controlsForm}>
        <FormRadioItem label={t('shared.projects.map.labels.noRadius')} value={NO_RADIUS} />
        {RADIUS_OPTIONS.map((r) => (
          <FormRadioItem key={r} label={t(SHOW_RADIUS_LABEL, { radius: r })} value={r.toString()} />
        ))}
      </FormRadioGroup>
    </SectionWrapper>
  );
};
